/**
 * usePushNotifications
 *
 * Registers for Apple Push Notifications via Capacitor's PushNotifications plugin.
 * Only runs on native iOS (no-ops in the web browser).
 *
 * On registration: sends the device token to POST /api/push/register.
 * On silent score-update push: dispatches a custom DOM event so Watch.tsx
 *   and other score components can trigger a data refresh without a full reload.
 *
 * Usage:
 *   import { usePushNotifications } from './hooks/usePushNotifications';
 *   // In your top-level App component:
 *   usePushNotifications(isAuthenticated);
 */

import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import api from '../api';

export function usePushNotifications(isLoggedIn: boolean) {
  useEffect(() => {
    // Push notifications only work on a real iOS device (Capacitor native platform)
    if (!isLoggedIn || !Capacitor.isNativePlatform()) return;

    // Lazily import so the web bundle doesn't fail if the package isn't installed yet
    import('@capacitor/push-notifications').then(({ PushNotifications }) => {
      PushNotifications.requestPermissions().then(result => {
        if (result.receive === 'granted') {
          PushNotifications.register();
        }
      });

      // Registration success — send token to server
      const regListener = PushNotifications.addListener('registration', token => {
        api.post('/push/register', { device_token: token.value, platform: 'ios' })
          .catch(() => { /* non-critical — fail silently */ });
      });

      // Registration failure — log only
      const errListener = PushNotifications.addListener('registrationError', err => {
        console.warn('[Push] Registration error:', err);
      });

      // Foreground push received (silent score update or analysis ready alert)
      const pushListener = PushNotifications.addListener('pushNotificationReceived', notification => {
        const data = (notification as any).data || {};
        if (data.type === 'score_update' && data.gameId) {
          // Let any listening component refresh its score data
          window.dispatchEvent(new CustomEvent('apns-score-update', { detail: { gameId: data.gameId } }));
        }
      });

      // Notification tapped from notification centre — navigate if needed
      const actionListener = PushNotifications.addListener('pushNotificationActionPerformed', action => {
        const data = action.notification?.data || {};
        if (data.gameId) {
          window.location.hash = `/watch/${data.gameId}`;
        }
      });

      return () => {
        regListener.then(l => l.remove());
        errListener.then(l => l.remove());
        pushListener.then(l => l.remove());
        actionListener.then(l => l.remove());
      };
    }).catch(() => { /* @capacitor/push-notifications not installed yet */ });
  }, [isLoggedIn]);
}
